import device from '@play-co/timestep-core/lib/device';
import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import uiConfig from './ui/config';
import StackView from '@play-co/timestep-core/lib/ui/StackView';
import { hideLoading, showLoading } from 'src/state/ui';
import AssetGroup from '@play-co/timestep-core/lib/ui/resource/AssetGroup';
import * as beautifyNumber from 'src/replicant/utils/beautifyNumber';
import supportEmail from 'src/replicant/ruleset/supportEmail';
import { createImageDescriptor } from '@play-co/timestep-core';
import SceneManager from 'src/game/logic/SceneManager';
import { Platform, PlatformLine, PlatformLineGuest } from '@play-co/gcinstant';
import { DEFAULT_TIMEZONE } from 'src/replicant/utils/time';

export type System = {
  start: () => void;
  stop: () => void;
};

let rootView: StackView;
let sceneManager: SceneManager;

// screen dimensions and scales

export const getApplicationScale = () => {
  return (device.height / uiConfig.height) * getExtraScale();
};

export const getExtraScale = () => {
  // calculate extra scaling for large devices
  // extraScale is based on different screen ratios,
  // starting from what we consider a `normal` one, which in this case is GalaxyS5 = 9:16 = 0.5625

  // also, extraScaling will never be over 1, which means that assets
  // won't scale up from their original size

  // Update: We will allow assets to scale up to maximum allowed scale,
  // mainly to compensate for fb top bar

  const maxAllowedScale = uiConfig.height / 1080;
  const screenRatio = device.width / device.height;
  const normalScreenRatio = 0.5625; // GalaxyS5 (9: 16)
  const extraScale = Math.min(
    (screenRatio * 1) / normalScreenRatio,
    maxAllowedScale,
  );

  return extraScale;
};

// from 3:4 to 9:16 = 0.5625
// from 9:16 to 3:4 = 0.8
export const mapScaleUpFactor = uiConfig.height / 1024;

export const getScreenDimensions = () => {
  const scale = getApplicationScale();
  const w = Math.ceil(device.width / scale);
  let h = Math.ceil(device.height / scale);
  return { width: w, height: h };
};

export const getScreenExtraVerticalSpace = () => {
  const screen = getScreenDimensions();
  return screen.height - uiConfig.height;
};

export const getScaleFactor = () => {
  const screen = getScreenDimensions();
  return Math.min(screen.height / uiConfig.height, 1);
};

export const getScreenTop = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.height - uiConfig.height;
  const screenTop = -Math.ceil(extraSpace / 2);

  // console.log('>>> screenTop', extraSpace, screenTop);
  return screenTop;
};

export const getScreenBottom = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.height - uiConfig.height;
  const screenBottom = uiConfig.height + Math.ceil(extraSpace / 2);

  // console.log('>>> screenBottom', extraSpace, screenBottom);
  return screenBottom;
};

export const getScreenLeft = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.width - uiConfig.width;
  const screenLeft = -Math.ceil(extraSpace / 2);

  // console.log('>>> screenLeft', extraSpace, screenLeft);
  return screenLeft;
};

export const getScreenRight = () => {
  const screen = getScreenDimensions();
  const extraSpace = screen.width - uiConfig.width;
  const screenRight = uiConfig.width + Math.ceil(extraSpace / 2);

  // console.log('>>> screenRight', extraSpace, screenRight);
  return screenRight;
};

// animation stuff
export const animDuration: number = 200;

// waiting functions
export const waitForIt = (cb: () => void, duration = animDuration, it?) => {
  it = it || {};

  animate(it)
    .clear()
    .wait(duration)
    .then(() => {
      animate(it).clear();
      cb();
    });
};

export const waitForItPromise = async (duration = animDuration) =>
  new Promise<void>((resolve) => waitForIt(resolve, duration));

export const createWaiter = () => {
  const handle = {};
  return {
    wait: (cb: () => void, duration = animDuration) =>
      waitForIt(cb, duration, handle),
    clear: () => animate(handle).clear(),
  };
};

export const createWaiterInView = (view: View) => {
  const waiter = createWaiter();

  view.on('ViewRemoved', () => void waiter.clear());

  return waiter;
};

export async function withLoading<T>(fn: () => Promise<T>): Promise<T> {
  StateObserver.dispatch(showLoading());

  try {
    return await fn();
  } finally {
    StateObserver.dispatch(hideLoading());
  }
}

export const waitForAssets = async (assets: AssetGroup) => {
  if (assets.isLoaded()) return;

  await withLoading(() => assets.load());
};

export const parseAmount = beautifyNumber.parseAmount;

export const toFixedTrunc = beautifyNumber.toFixedTrunc;

export const toShort = beautifyNumber.toShort;

export const toAmountShort = (amount: number) =>
  beautifyNumber.toAmountShort(amount, StateObserver.getState().ui.locale);

export const serverSyncedLocalTime = () =>
  StateObserver.now() - new Date().getTimezoneOffset() * 60 * 1000;

export function setRootView(view: StackView) {
  rootView = view;
}

export function getRootView(): StackView {
  return rootView;
}

export function setSceneManager(manager: SceneManager) {
  sceneManager = manager;
}

export function getSceneManager(): SceneManager {
  return sceneManager;
}

// Wait and block taps for a specified amount of time
export async function blockUI(millis: number) {
  const blockEvents = new View({
    superview: getRootView(),
    infinite: true,
    zIndex: 100000,
  });

  await waitForItPromise(millis);

  blockEvents.removeFromSuperview();
}

export const isPlatformLine = (platform: Platform): platform is PlatformLine =>
  platform.platformID === 'line';
export const isPlatformGuest = (
  platform: Platform,
): platform is PlatformLineGuest => platform.platformID === 'line-guest';

export function getUrlSearchParamValue(paramName: string) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

export function getSupportMail(): string {
  const platform =
    process.env.LINE_GUEST === 'true' ? 'line' : process.env.PLATFORM;
  switch (platform) {
    case 'line': {
      return supportEmail.bd;
    }
    case 'mock':
    case 'web': {
      return supportEmail.web;
    }
    default:
      throw new Error(`Unknown platform: ${process.env.PLATFORM}`);
  }
}

export function canOpenExternalWindow() {
  // Desktop, native, liff can open window
  return true;
}

export function getCreativeSuffix() {
  return StateObserver.getState().user.settings.locale === 'ja' ? 'jp' : 'en';
}

/**
 * Preloads an array of asset urls that can be `null` or `undefined`.
 * Useful when preloading user icons that can be nulls.
 */
export async function preloadImages(
  assetUrls: (string | null | undefined)[],
): Promise<void> {
  await Promise.all(
    assetUrls.filter((x) => !!x).map((x) => createImageDescriptor(x)),
  );
}

/**
 * Split array by chunks
 */
export function chunk<T>(data: T[], size: number): T[][] {
  const results = [];

  for (let i = 0; i < data.length; i += size) {
    const chunk = data.slice(i, i + size);
    results.push(chunk);
  }

  return results;
}

export async function delay(ms: number): Promise<void> {
  if (ms === 0) return;
  return new Promise((res) => setTimeout(res, ms));
}

export async function retry<T>(
  action: () => Promise<T>,
  options?: {
    /** @defaultValue 2 */
    retryCount?: number;

    /** @defaultValue 500 */
    linearBackoff?: number;
  },
): Promise<T> {
  const retryCount = options?.retryCount ?? 2;

  const backoff = options?.linearBackoff ?? 500;
  let currentRetry = 0;

  do {
    try {
      return await action();
    } catch (error) {
      if (currentRetry >= retryCount) {
        throw error;
      } else {
        currentRetry++;
        await delay((currentRetry - 1) * backoff);
      }
    }
  } while (true); // eslint-disable-line no-constant-condition
}

export function getClientTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? DEFAULT_TIMEZONE;
}
